/**
 * HelpScreen — static FAQ overlay at /help.
 *
 * Styled to prototype.jsx HelpScreen (screens.jsx:894-921):
 *   - PageHeader with subtitle "Частые вопросы"
 *   - FAQ cards: T.headline question / T.subhead answer
 *   - Ink-dark support CTA card with terracotta PressBtn
 *
 * Uses StaticPage atom for layout. usePlatformBack() registers navigate(-1)
 * on the TG BackButton. The same navigate(-1) callback is passed to
 * StaticPage.onBack so the visual back pill in PageHeader is consistent.
 *
 * Content is hardcoded inline as JSX (no ru.json, no useT) — per FTR-998
 * HomeScreen / inline-JSX convention.
 *
 * Uses:
 *   - components/atoms: StaticPage, Card, PressBtn
 *   - hooks/usePlatformBack: TG back-button registration
 *   - hooks/usePlatform: openExternal deep-link
 *   - platform/links: BOT_LINK
 *   - react-router-dom: useNavigate
 *
 * Used by:
 *   - App.tsx (route /help, sibling of AppShell)
 */
import { useNavigate } from 'react-router-dom';
import { StaticPage, Card, PressBtn } from '../components/atoms';
import { usePlatformBack } from '../hooks/usePlatformBack';
import { usePlatform } from '../providers/PlatformProvider';
import { P, S, T } from '../styles/tokens';
import { BOT_LINK } from '../platform/links';

// ─── FAQ data (hardcoded inline — no i18n) ───────────────────────────────────

const FAQS = [
  {
    q: 'Когда придут деньги?',
    a: 'После принятия отзыва — через 14 дней (правила маркетплейса).',
  },
  {
    q: 'Что если скриншот отклонили?',
    a: 'У тебя 5 попыток. Внимательно читай подсказку и пример.',
  },
  {
    q: 'Можно ли вернуть товар?',
    a: 'Нет. Возврат отменит кешбэк, придётся вернуть деньги в Awardy.',
  },
  {
    q: 'Не успела за 30 минут с заказом?',
    a: 'Задание сгорит. Пиши в чат — иногда продлеваем.',
  },
] as const;

// ─── HelpScreen ───────────────────────────────────────────────────────────────

export default function HelpScreen() {
  const navigate = useNavigate();
  const platform = usePlatform();
  const onBack = () => navigate(-1);

  // Registers navigate(-1) on the TG BackButton for hardware/OS back gestures.
  usePlatformBack();

  return (
    <StaticPage title="Помощь" subtitle="Частые вопросы" onBack={onBack}>
      {FAQS.map((faq) => (
        <Card key={faq.q} p={14} r={20}>
          <div
            style={{
              font: T.headline,
              color: S.label,
              letterSpacing: -0.43,
              marginBottom: 6,
            }}
          >
            {faq.q}
          </div>
          <div
            style={{
              font: T.subhead,
              color: S.secondaryLabel,
              lineHeight: 1.5,
            }}
          >
            {faq.a}
          </div>
        </Card>
      ))}

      {/* Support CTA — ink-dark card with terracotta primary button */}
      <Card
        p={16}
        r={20}
        style={{
          marginTop: 8,
          background: P.ink,
          border: 'none',
          textAlign: 'center' as const,
          boxShadow: '0 4px 16px rgba(45,37,24,0.18)',
        }}
      >
        <div
          style={{
            font: T.headline,
            color: S.labelDark,
            letterSpacing: -0.43,
          }}
        >
          Не нашла ответ?
        </div>
        <div
          style={{
            font: T.footnote,
            color: S.secondaryLabelDark,
            marginTop: 4,
          }}
        >
          Напиши в чат — отвечаем за 5 минут
        </div>
        <div style={{ marginTop: 12 }}>
          <PressBtn
            bg={P.terracotta}
            // eslint-disable-next-line no-restricted-syntax -- TECH-1036 §7: legitimate support deep-link (FAQ → бот)
            onClick={() => platform.openExternal(BOT_LINK)}
          >
            Написать в поддержку
          </PressBtn>
        </div>
      </Card>
    </StaticPage>
  );
}
